// src/services/updateService.js
// Renderer-side wrapper for the in-app update flow (autosigner). See
// docs/exec-plans/active/2026-06-23-app-update-flow.md.
//
// IMPORTANT: the `compareVersions` helper here is a MIRROR of the one
// in electron/updater.js. Main is the single source of truth for the
// boolean `isNewer` returned in every IPC payload. The renderer
// version exists so the UI can:
//   * render the comparison pill from the values main already returned
//   * support manual display when IPC isn't available (dev/web)
// Do not diverge from the main-process implementation. If the algorithm
// ever changes, update BOTH files in the same commit.

/**
 * Compare two semver-like version strings segment by segment.
 * Mirror of compareVersions in electron/updater.js — DO NOT diverge.
 * @param {string} a - First version, e.g. "2.1.0"
 * @param {string} b - Second version, e.g. "2.1.0"
 * @returns {number} 1 if a > b, -1 if a < b, 0 if equal
 */
export const compareVersions = (a, b) => {
  const parse = (v) => String(v || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const [aParts, bParts] = [parse(a), parse(b)];
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const av = aParts[i] || 0;
    const bv = bParts[i] || 0;
    if (av > bv) return 1;   // a > b
    if (av < bv) return -1;  // a < b
  }
  return 0;                  // equal
};

/**
 * Returns true when `latest` is strictly newer than `current`.
 * @param {string} latest
 * @param {string} current
 * @returns {boolean}
 */
export const isNewerVersion = (latest, current) => compareVersions(latest, current) > 0;

/**
 * Returns one of:
 *   - 'up-to-date'   when current === latest
 *   - 'update-available' when current < latest
 *   - 'local-newer'  when current > latest (downgrade ignored)
 * @param {string} latest
 * @param {string} current
 * @returns {'up-to-date' | 'update-available' | 'local-newer'}
 */
export const comparisonState = (latest, current) => {
  const cmp = compareVersions(latest, current);
  if (cmp > 0) return 'update-available';
  if (cmp < 0) return 'local-newer';
  return 'up-to-date';
};

/**
 * Returns the window.updateAPI surface if Electron preload has exposed
 * it. Returns null on the web build or in dev when the API isn't there.
 * @returns {Object|null}
 */
const getAPI = () => {
  if (typeof window === 'undefined') return null;
  return window.updateAPI || null;
};

/**
 * Has Electron preloaded the update API? Use this to gate UI features.
 * @returns {boolean}
 */
export const isUpdateApiAvailable = () => !!getAPI();

/**
 * Per-app gate predicate for Restaurant POS.
 *
 * Two halves, both must be clear before install:
 *   1. `tableSession.hasOpenTables()` — block if any table is open.
 *   2. `outbox.hasPendingSync()` — block if any outbox row is still
 *      in pending / timeout / failed / sending state.
 *
 * The shape is shared across apps so the UpdatesView can render the
 * same banner copy with the same data. The banner copy here is:
 *   "N open table(s) / N offline invoice(s). Close them before installing."
 *
 * @returns {Promise<{blocked: boolean, reason?: string, pendingCount?: number, openTableCount?: number, route?: string, routeLabel?: string}>}
 */
export const evaluateUpdateGate = async () => {
  let blocked = false;
    let pendingCount = 0;
    let openTableCount = 0;
    const reasons = [];

    try {
        const outbox = await import(/* webpackChunkName: "outbox-gate" */ './outbox');
        if (typeof outbox.hasPendingSync === 'function') {
            const pending = await outbox.hasPendingSync();
            if (pending) {
                blocked = true;
                if (typeof outbox.countPending === 'function') {
                    try { pendingCount = await outbox.countPending(); } catch (_) { /* ignore */ }
                }
                reasons.push(`${pendingCount} offline invoice${pendingCount === 1 ? '' : 's'}`);
            }
        }
    } catch (e) {
        console.warn('[updateService] Outbox gate check failed:', e && e.message);
    }

    try {
        const tableSession = await import(/* webpackChunkName: "table-session-gate" */ './tableSession');
        if (typeof tableSession.hasOpenTables === 'function') {
            const open = await tableSession.hasOpenTables();
            if (open) {
                blocked = true;
                if (typeof tableSession.countOpenTables === 'function') {
                    try { openTableCount = await tableSession.countOpenTables(); } catch (_) { /* ignore */ }
                }
                reasons.push(`${openTableCount} open table${openTableCount === 1 ? '' : 's'}`);
            }
        }
    } catch (e) {
        console.warn('[updateService] Table session gate check failed:', e && e.message);
    }

    if (!blocked) {
        return { blocked: false };
    }

    return {
        blocked: true,
        reason: `${reasons.join(' / ')}. Close them before installing.`,
        pendingCount,
        openTableCount,
        // Restaurant POS gate spans two screens; route the user to the
        // table list by default (the operator can also see pending
        // invoices in the same drawer). Override per-screen from the
        // banner link if you want to deep-link elsewhere.
        route: 'tables',
        routeLabel: 'Tables'
    };
};

/**
 * Trigger a check. Always returns the cache snapshot from main (or an
 * empty object on failure / non-Electron). Calling this does NOT
 * return the event listeners — register them via `init` for that.
 *
 * @returns {Promise<Object>}
 */
export const checkForUpdate = async () => {
  const api = getAPI();
  if (!api) return {};
  try {
    const res = await api.check();
    return res || {};
  } catch (e) {
    console.warn('[updateService] check failed:', e && e.message);
    return { ok: false, error: String(e && e.message ? e.message : e) };
  }
};

/**
 * Cached snapshot from main (current/latest versions and isNewer).
 * @returns {Promise<{currentVersion?: string, latestVersion?: string, isNewer?: boolean, lastCheckedAt?: string}>}
 */
export const getCachedUpdate = async () => {
  const api = getAPI();
  if (!api) return {};
  try { return (await api.getCachedUpdate()) || {}; } catch (_) { return {}; }
};

/**
 * Local current version, exposed via IPC. Useful for the header.
 * @returns {Promise<string>}
 */
export const getCurrentVersion = async () => {
  const api = getAPI();
  if (!api) {
    // Non-Electron: fall back to package.json via require.
    try {
      // eslint-disable-next-line global-require
      const pkg = require('../../package.json');
      return pkg && pkg.version ? pkg.version : '';
    } catch (_) {
      return '';
    }
  }
  try {
    const res = await api.getCurrentVersion();
    return (res && res.currentVersion) || '';
  } catch (_) {
    return '';
  }
};

/**
 * Download the latest installer. Streams to a temp file in main,
 * verifies SHA256, then resolves with `{ path, sha256Ok, version }`.
 * @returns {Promise<{ok: boolean, path?: string, sha256Ok?: boolean, version?: string, code?: string, message?: string}>}
 */
export const downloadUpdate = async () => {
  const api = getAPI();
  if (!api) return { ok: false, code: 'NO_API', message: 'updateAPI not available' };
  try {
    return (await api.download()) || { ok: false };
  } catch (e) {
    return { ok: false, code: 'EXCEPTION', message: String(e && e.message ? e.message : e) };
  }
};

/**
 * Launch the verified installer and relaunch the app. Re-runs the gate
 * in main as defence-in-depth before relaunching.
 * @returns {Promise<{ok: boolean, code?: string, message?: string}>}
 */
export const installUpdate = async () => {
  const api = getAPI();
  if (!api) return { ok: false, code: 'NO_API', message: 'updateAPI not available' };
  try {
    return (await api.install()) || { ok: false };
  } catch (e) {
    return { ok: false, code: 'EXCEPTION', message: String(e && e.message ? e.message : e) };
  }
};

/**
 * Install the Vuex wiring. Call once on App mount. Pass the Vuex store
 * (typically `this.$store` or the imported `store`). Returns an
 * unsubscribe function.
 *
 * @param {Object} store - Vuex store
 * @returns {Function} unsubscribe
 */
export const init = (store) => {
  const api = getAPI();
  if (!api || !store) return () => { /* no-op */ };

  const applyMeta = (meta) => {
    if (!meta) return;
    store.commit('updates/setCurrentVersion', meta.currentVersion || '');
    store.commit('updates/setLatestVersion', meta.latestVersion || '');
    store.commit('updates/setIsNewer', !!meta.isNewer);
    if (meta.lastCheckedAt) store.commit('updates/setLastCheckedAt', meta.lastCheckedAt);
    if (meta.meta) store.commit('updates/setMeta', meta.meta);
  };

  const offAvailable = api.onAvailable((payload) => {
    applyMeta(payload);
    store.commit('updates/setAvailable', true);
  });
  const offNotAvailable = api.onNotAvailable((payload) => {
    applyMeta(payload);
    store.commit('updates/setAvailable', false);
  });
  const offProgress = api.onProgress((payload) => {
    if (!payload) return;
    store.commit('updates/setDownloading', true);
    store.commit('updates/setProgressPercent', Math.max(0, Math.min(100, Number(payload.percent) || 0)));
  });
  const offDownloaded = api.onDownloaded((payload) => {
    if (!payload) return;
    store.commit('updates/setDownloading', false);
    store.commit('updates/setDownloadedPath', payload.path || '');
    store.commit('updates/setDownloadedShaOk', !!payload.sha256Ok);
  });
  const offError = api.onError((payload) => {
    if (!payload) return;
    store.commit('updates/setDownloading', false);
    store.commit('updates/setError', { code: payload.code || 'UNKNOWN', message: payload.message || '' });
  });

  // Populate the header immediately from the cache so the screen
  // doesn't flash empty values.
  try {
    api.getCachedUpdate().then((cached) => {
      if (cached && cached.ok !== false) {
        applyMeta(cached);
        store.commit('updates/setAvailable', !!cached.isNewer);
      }
    }).catch(() => { /* ignore */ });
  } catch (_) { /* ignore */ }

  // Returns a single unsubscribe that detaches all listeners.
  return () => {
    try { offAvailable && offAvailable(); } catch (_) { /* ignore */ }
    try { offNotAvailable && offNotAvailable(); } catch (_) { /* ignore */ }
    try { offProgress && offProgress(); } catch (_) { /* ignore */ }
    try { offDownloaded && offDownloaded(); } catch (_) { /* ignore */ }
    try { offError && offError(); } catch (_) { /* ignore */ }
  };
};

export default {
  compareVersions,
  isNewerVersion,
  comparisonState,
  isUpdateApiAvailable,
  evaluateUpdateGate,
  checkForUpdate,
  getCachedUpdate,
  getCurrentVersion,
  downloadUpdate,
  installUpdate,
  init
};